import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { useAdminLeads, useUpdateLeadStatus } from "@/hooks/useAdmin";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import { cn, formatDate } from "@/lib/utils";
import type { AdminLeadDto } from "@/types/api";

const STATUS_OPTIONS: Array<AdminLeadDto["status"]> = ["new", "contacted", "quoted", "won", "lost"];

export function AdminLeadsPage() {
  const [statusFilter, setStatusFilter] = React.useState<string>("");
  const [page, setPage] = React.useState(1);

  const { data, isLoading } = useAdminLeads({ status: statusFilter, page, pageSize: 50 });

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">Leads</h2>
          <p className="text-sm text-muted-foreground">Portfolio Custom enquiries — work the queue.</p>
        </div>
        <div className="flex flex-wrap gap-1">
          <Filter label="All" active={statusFilter === ""}     onClick={() => { setStatusFilter("");          setPage(1); }} />
          {STATUS_OPTIONS.map(s => (
            <Filter key={s} label={cap(s)} active={statusFilter === s} onClick={() => { setStatusFilter(s); setPage(1); }} />
          ))}
        </div>
      </div>

      {isLoading ? (
        <Card><CardContent className="space-y-2 py-5">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
        </CardContent></Card>
      ) : !data || data.items.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
          {statusFilter ? `No leads in "${statusFilter}".` : "No leads yet."}
        </CardContent></Card>
      ) : (
        <>
          <Card className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contact</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Domain</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Received</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map(lead => <LeadRow key={lead.id} lead={lead} />)}
              </TableBody>
            </Table>
          </Card>
          <div className="grid gap-3 md:hidden">
            {data.items.map(lead => <LeadCard key={lead.id} lead={lead} />)}
          </div>
        </>
      )}
    </div>
  );
}

function LeadRow({ lead }: { lead: AdminLeadDto }) {
  return (
    <TableRow>
      <TableCell>
        <p className="font-medium">{lead.full_name}</p>
        <p className="text-xs text-muted-foreground">
          {lead.email}{lead.company_name ? ` · ${lead.company_name}` : ""}
        </p>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">{lead.plan_slug ?? "—"}</TableCell>
      <TableCell className="text-sm text-muted-foreground">{lead.domain_hint ?? "—"}</TableCell>
      <TableCell><StatusBadge status={lead.status} /></TableCell>
      <TableCell className="text-sm text-muted-foreground">{formatDate(lead.created_at)}</TableCell>
      <TableCell><StatusActions lead={lead} /></TableCell>
    </TableRow>
  );
}

function LeadCard({ lead }: { lead: AdminLeadDto }) {
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate font-medium">{lead.full_name}</p>
            <p className="truncate text-xs text-muted-foreground">{lead.email}</p>
          </div>
          <StatusBadge status={lead.status} />
        </div>
        {lead.notes && <p className="text-xs text-muted-foreground line-clamp-2">{lead.notes}</p>}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
          <p className="text-[11px] text-muted-foreground">
            {lead.plan_slug ?? "no plan"} · {formatDate(lead.created_at)}
          </p>
          <StatusActions lead={lead} />
        </div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status }: { status: string }) {
  const variant: React.ComponentProps<typeof Badge>["variant"] =
    status === "won"        ? "success"
    : status === "lost"     ? "destructive"
    : status === "new"      ? "default"
    : "outline";
  return <Badge variant={variant} className="uppercase">{status}</Badge>;
}

function StatusActions({ lead }: { lead: AdminLeadDto }) {
  const update = useUpdateLeadStatus(lead.id);
  const { toast } = useToast();
  async function set(next: string) {
    if (next === lead.status) return;
    try {
      await update.mutateAsync(next);
      toast({ title: `Marked ${next}`, variant: "success" });
    } catch (err) {
      toast({
        title: "Update failed",
        description: err instanceof ApiError ? err.message : "Try again.",
        variant: "destructive"
      });
    }
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" disabled={update.isPending}>Change</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {STATUS_OPTIONS.map(s => (
          <DropdownMenuItem key={s} onClick={() => void set(s)}>{cap(s)}</DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Filter({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
        active ? "border-brand-accent bg-brand-accentSoft text-brand-accentDark"
               : "border-border text-muted-foreground hover:bg-muted"
      )}
    >{label}</button>
  );
}

function cap(s: string) { return s.charAt(0).toUpperCase() + s.slice(1); }
